import React, { useState, useEffect } from "react";
import { IconType } from "react-icons";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/common/modal";
import { InputV2 } from "components/common/InputV2/InputV2";
import { Button } from "components/common/buttons";
import LoaderV2 from "components/common/LoaderV2";

interface ButtonProps {
  onClick?: VoidFunction;
  text: string;
  icon?: IconType;
}

interface TConfirmationModalProps extends PropsWithRequiredChildren {
  isOpen?: boolean;
  title: string;
  confirmationPhrase?: string;
  isLoading?: boolean;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
}

const ConfirmationModal = ({
  isOpen,
  title,
  children,
  confirmationPhrase,
  isLoading,
  confirmButtonProps,
  cancelButtonProps,
}: TConfirmationModalProps) => {
  const [confirmationValue, setConfirmationValue] = useState<string>("");
  const [confirmationError, setConfirmationError] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const {
    text: confirmBtnText = "Confirm",
    icon: ConfirmIcon,
    onClick: onConfirm,
  } = confirmButtonProps ?? {};

  const { text: cancelBtnText = "Cancel", onClick: onClose } = cancelButtonProps ?? {};

  // reset state
  useEffect(() => {
    setIsSubmitted(false);
    setConfirmationValue("");
    setConfirmationError("");
  }, [isOpen]);

  useEffect(() => {
    if (!confirmationPhrase || !isSubmitted) return;

    if (!confirmationValue) {
      setConfirmationError("This is a required field");
      return;
    }

    if (confirmationValue !== confirmationPhrase) {
      setConfirmationError(`You should type ${confirmationPhrase} to confirm`);
      return;
    }

    setConfirmationError("");
  }, [confirmationValue, confirmationPhrase, isSubmitted]);

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnConfirm = () => {
    setIsSubmitted(true);

    if (!confirmationPhrase || confirmationValue === confirmationPhrase) {
      onConfirm?.();
    }
  };

  return (
    <Modal isOpen={!!isOpen} onClose={handleOnClose}>
      <ModalHeader title={title} onClose={handleOnClose} />
      <ModalBody>
        {children}
        {confirmationPhrase && (
          <>
            <InputV2
              id="confirmationBtn"
              value={confirmationValue}
              onChange={(e) => {
                setConfirmationValue(e.target.value);
              }}
              placeholder={`Type "${confirmationPhrase}" to confirm`}
              inputProps={{
                autoComplete: "off",
              }}
            />
            {confirmationError && <p className="text-danger text-xs">{confirmationError}</p>}
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button size="lg" variant="ghostSecondary" onClick={handleOnClose}>
          {cancelBtnText}
        </Button>
        <Button variant="badge" color="brand" onClick={handleOnConfirm}>
          {ConfirmIcon && <ConfirmIcon />}
          {confirmBtnText}
        </Button>
      </ModalFooter>
      {isLoading && <LoaderV2 />}
    </Modal>
  );
};

export default ConfirmationModal;
